import React, { useEffect, useState, useRef } from "react";
import Head from "next/head";
import { FaFileUpload } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import { GiExpand } from "react-icons/gi";
import { PDFDocument } from "pdf-lib";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";
import Image from "next/image";

GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const BlogData = [
  {
    id: 1,
    question: "What is Crop PDF Tool?",
    answer: (
      <div className="">
        <p className="">
          A Crop PDF Tool is a tool that allows you to trim or remove unwanted
          borders, edges, or content from a PDF document. With this tool, you
          can adjust the visible area of the PDF pages according to your
          preferences.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    question: "How to Use the Crop PDF Tool?",
    answer: (
      <div className="">
        <p className="">
          To use the Crop PDF Tool, you need to upload the PDF file you want to
          edit. Then, you can select the area you want to keep and crop the PDF
          pages accordingly. The tool will save the modified PDF with the
          selected areas cropped.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    question: "Is there a Limit on the Number of Pages I Can Crop?",
    answer: (
      <div className="">
        <p className="">
          There is typically no limit on the number of pages you can crop using
          the Crop PDF Tool. You can adjust the visible area of as many pages as
          needed in your PDF document.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    question: "Are Cropped Areas Permanently Removed?",
    answer: (
      <div className="">
        <p className="">
          Yes, when you use the Crop PDF Tool, the selected areas are
          permanently removed from the visible content of the PDF file. Make
          sure to review your selections before confirming the cropping.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    question: "Can I Preview the PDF Before Cropping Areas?",
    answer: (
      <div className="">
        <p className="">
          Many Crop PDF Tools offer a preview feature that allows you to view
          the PDF before making any changes. This helps you ensure you are
          cropping the correct areas from the document.
        </p>
      </div>
    ),
  },
];

const CropPdf = () => {
  const previewRef = useRef();

  const [pdf, setPdf] = useState(null);
  const [cropData, setCropData] = useState({
    width: 0,
    height: 0,
    leftPosition: 0,
    bottomPosition: 0,
    rightPosition: 0,
    topPosition: 0,
  });
  const [images, setImages] = useState([]);
  const [pageRendering, setPageRendering] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [showZoomIcon, setShowZoomIcon] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [signaturePopup, setSignaturePopup] = useState(false);

  const scrollToPreview = () => {
    const element = document.getElementById("preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer.files[0]) return;
    const file = e.dataTransfer.files[0];
    uploadPdf(file);
    scrollToPreview();
  };

  const handlePdfUpload = async (e) => {
    if (!e.target.files[0]) return;
    const file = e.target.files[0];
    uploadPdf(file);
    scrollToPreview();
  };

  const uploadPdf = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const pdfDoc = await PDFDocument.load(e.target.result);
      setPdf(pdfDoc);
    };
    reader.readAsArrayBuffer(file);

    try {
      setPageRendering(true);
      const imagesList = [];
      const uri = URL.createObjectURL(file);
      const PDF_DOC = await getDocument(uri).promise;
      for (let i = 1; i <= PDF_DOC.numPages; i++) {
        const page = await PDF_DOC.getPage(i);
        const viewport = page.getViewport({ scale: 1 });
        const canvas = document.createElement("canvas");
        const canvasContext = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext, viewport }).promise;
        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        imagesList.push(imgData);
      }
      setImages(imagesList);
      setPageRendering(false);
    } catch (error) {
      setPageRendering(false);
      console.log(error);
    }
  };

  const handleCropPdf = async () => {
    setDownloading(true);
    const parseOrZero = (value) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    let leftPosition = parseOrZero(cropData.leftPosition);
    let bottomPosition = parseOrZero(cropData.bottomPosition);
    let rightPosition = parseOrZero(cropData.rightPosition);
    let topPosition = parseOrZero(cropData.topPosition);
    let width = parseOrZero(cropData.width);
    let height = parseOrZero(cropData.height);

    try {
      const pdfDoc = pdf;
      const pages = await pdfDoc.getPages();
      for (const page of pages) {
        page.setCropBox(
          leftPosition,
          bottomPosition,
          leftPosition + width,
          bottomPosition + height,
        );
        page.setSize(width - rightPosition, height - topPosition);
      }
      const pdfBytes = await pdfDoc.save();
      downloadCroppedPdf(pdfBytes);
    } catch (error) {
      setDownloading(false);
    }
  };

  const downloadCroppedPdf = async (docBytes) => {
    const blob = new Blob([docBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "cropped.pdf";
    link.click();
    setDownloading(false);
    setSignaturePopup(true);
  };

  const handleDrag = (dragLine) => (e) => {
    switch (dragLine) {
      case "left":
        if (e.clientX - previewRef.current.offsetLeft < 0) {
          setCropData({
            ...cropData,
            leftPosition: 0,
          });
          return;
        }
        setCropData({
          ...cropData,
          leftPosition: e.clientX - previewRef.current.offsetLeft,
        });
        break;
      case "right":
        if (
          previewRef.current.offsetWidth -
            (e.clientX - previewRef.current.offsetLeft) <
          0
        ) {
          setCropData({
            ...cropData,
            rightPosition: 0,
          });
          return;
        }
        setCropData({
          ...cropData,
          rightPosition:
            previewRef.current.offsetWidth -
            (e.clientX - previewRef.current.offsetLeft),
        });
        break;
      case "top":
        if (e.clientY - previewRef.current.offsetTop < 0) {
          setCropData({
            ...cropData,
            topPosition: 0,
          });
          return;
        }
        setCropData({
          ...cropData,
          topPosition: e.clientY - previewRef.current.offsetTop,
        });
        break;
      case "bottom":
        if (
          previewRef.current.offsetHeight -
            (e.clientY - previewRef.current.offsetTop) <
          0
        ) {
          setCropData({
            ...cropData,
            bottomPosition: 0,
          });
          return;
        }
        setCropData({
          ...cropData,
          bottomPosition:
            previewRef.current.offsetHeight -
            (e.clientY - previewRef.current.offsetTop),
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (pdf) {
      const dimensions = pdf.getPages()[0];
      setCropData({
        width: dimensions.getWidth(),
        height: dimensions.getHeight(),
        leftPosition: 0,
        bottomPosition: 0,
        rightPosition: 0,
        topPosition: 0,
      });
    }
  }, [pdf]);

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>Crop PDF Tool - BoloForms</title>
        <meta
          name="description"
          content="Trim or remove unwanted borders, edges, or content from your PDF. Adjust the visible area of the PDF pages with the Crop PDF Tool."
        />
        <meta
          name="keywords"
          content="crop pdf, trim pdf, remove borders from pdf, adjust pdf pages, pdf content editing, pdf cropping tool, pdf crop, crop pdf tool, crop pdf online, crop pdf file, crop pdf pages, crop pdf document, crop pdf pages online, crop pdf pages free, crop pdf pages free online, crop pdf"
        />
        <meta name="author" content="BoloForms" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/crop-pdf"
        />
        <meta property="og:title" content="Crop PDF Tool - BoloForms" />
        <meta
          property="og:description"
          content="Trim or remove unwanted borders, edges, or content from your PDF. Adjust the visible area of the PDF pages with the Crop PDF Tool."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/crop-pdf"
        />
        <meta property="og:site_name" content="BoloForms" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="BoloForms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BoloForms" />
        <meta name="twitter:creator" content="@BoloForms" />
        <meta name="twitter:title" content="Crop PDF Tool - BoloForms" />
        <meta
          name="twitter:description"
          content="Trim or remove unwanted borders, edges, or content from your PDF. Adjust the visible area of the PDF pages with the Crop PDF Tool."
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/crop-pdf/"
        />
      </Head>

      <div className="">
        <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
          <div className="text-violet-500 text-center">
            <h1 className="text-3xl md:text-6xl text-gray-800">
              Crop PDF Tool
            </h1>
            <p className="text-lg md:text-2xl mt-4 text-gray-600">
              Crop PDF tool allows you to crop your PDF file and save it as a
              new PDF file.
            </p>
          </div>
          <div
            className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
            onDragEnter={(e) => e.preventDefault()}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">
              <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
              <div className="input_field flex flex-col w-full mx-auto text-center">
                <label>
                  <input
                    onChange={handlePdfUpload}
                    className="text-sm cursor-pointer w-36 hidden"
                    type="file"
                    accept=".pdf"
                  />
                  <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                    Upload PDF File
                  </div>
                </label>

                <div className="title text-gray-500 uppercase text-lg md:text-xl">
                  or drop file here
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          id="preview-pdfs"
          className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center"
        >
          {pdf && (
            <>
              <h1 className="text-3xl font-light text-center text-gray-700">
                Click on any page to crop
              </h1>

              <div className="relative w-full h-[400px] flex flex-wrap justify-center gap-4 border-2 md:px-10 py-4 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px] transition-all duration-300">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="border drop-shadow-2xl w-fit relative flex justify-center items-center flex-wrap"
                    onMouseOver={() => setShowZoomIcon(index + 1)}
                    onMouseLeave={() => setShowZoomIcon(null)}
                  >
                    <div className="border-1 drop-shadow-lg p-4 bg-white w-[230px]">
                      <Image
                        width={10}
                        height={10}
                        id="image-generated"
                        src={image}
                        alt="pdfImage"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                      {index + 1}
                    </div>
                    {showZoomIcon === index + 1 && (
                      <div
                        onClick={() => setPopUp(index + 1)}
                        className="absolute w-full h-full bg-gray-200/50 flex justify-center items-center cursor-pointer"
                      >
                        <div
                          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-gray-400 rounded-full text-xl text-white flex justify-center items-center hover:scale-105`}
                        >
                          <GiExpand />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div
                  id="page-loader"
                  className={`${
                    pageRendering
                      ? "absolute w-full top-0 left-0 h-full flex justify-center items-center bg-white z-50"
                      : "hidden"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    className="w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-violet-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex justify-center items-center flex-wrap text-center">
                <div className="">
                  <h4 className="text-gray-600 uppercase font-bold">Left</h4>
                  <input
                    type="text"
                    className="outline-none border w-[150px] text-center border-gray-300 p-2 text-gray-600 text-xl"
                    placeholder="Left"
                    value={cropData.leftPosition}
                    onChange={(e) =>
                      setCropData({
                        ...cropData,
                        leftPosition: e.target.value < 0 ? "0" : e.target.value,
                      })
                    }
                  />
                </div>
                <div className="">
                  <h4 className="text-gray-600 uppercase font-bold">Bottom</h4>
                  <input
                    type="text"
                    className="outline-none border w-[150px] text-center border-gray-300 p-2 text-gray-600 text-xl"
                    placeholder="Bottom"
                    value={cropData.bottomPosition}
                    onChange={(e) =>
                      setCropData({
                        ...cropData,
                        bottomPosition:
                          e.target.value < 0 ? "0" : e.target.value,
                      })
                    }
                  />
                </div>
                <div className="">
                  <h4 className="text-gray-600 uppercase font-bold">Right</h4>
                  <input
                    type="text"
                    className="outline-none border w-[150px] text-center border-gray-300 p-2 text-gray-600 text-xl"
                    placeholder="Right"
                    value={cropData.rightPosition}
                    onChange={(e) =>
                      setCropData({
                        ...cropData,
                        rightPosition:
                          e.target.value < 0 ? "0" : e.target.value,
                      })
                    }
                  />
                </div>
                <div className="">
                  <h4 className="text-gray-600 uppercase font-bold">Top</h4>
                  <input
                    type="text"
                    className="outline-none border w-[150px] text-center border-gray-300 p-2 text-gray-600 text-xl"
                    placeholder="Top"
                    value={cropData.topPosition}
                    onChange={(e) =>
                      setCropData({
                        ...cropData,
                        topPosition: e.target.value < 0 ? "0" : e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              {
                <div className="">
                  <button
                    onClick={handleCropPdf}
                    className="bg-violet-600 mx-auto w-full sm:w-[350px] px-2 py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                  >
                    {downloading ? (
                      <div
                        className={`flex items-center text-white space-x-2 ${styles.loader}`}
                      >
                        <div className="">Downloading</div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      </div>
                    ) : (
                      <>Download Cropped PDF</>
                    )}
                  </button>
                </div>
              }
            </>
          )}
        </div>
        {popUp && (
          <div className="bg-gray-600/75 w-full h-full fixed z-[1000] left-0 top-0 p-4 flex justify-center items-center">
            <div
              onClick={() => setPopUp(null)}
              className="absolute top-[20px] right-[20px] hover:opacity-90 cursor-pointer grid place-items-center text-white font-bold text-3xl md:text-5xl"
            >
              <AiOutlineClose className="font-bold" />
            </div>
            <div ref={previewRef} className="relative">
              <Image
                src={images[popUp - 1]}
                alt="pdfImage"
                className={`w-${cropData.width} h-${cropData.height} mx-auto`}
                width={cropData.width}
                height={cropData.height}
              />
              <div
                className="absolute top-0 w-[2px] h-full bg-violet-500 cursor-col-resize"
                onDragEnd={handleDrag("left")}
                style={{ left: `${cropData.leftPosition}px` }}
                draggable
              ></div>
              <div
                className="absolute left-0 w-full h-[2px] bg-violet-500 cursor-row-resize"
                onDragEnd={handleDrag("bottom")}
                style={{ bottom: `${cropData.bottomPosition}px` }}
                draggable
              ></div>
              <div
                className="absolute top-0 right-[5px] w-[2px] h-full bg-violet-500 cursor-col-resize"
                onDragEnd={handleDrag("right")}
                style={{ right: `${cropData.rightPosition}px` }}
                draggable
              ></div>
              <div
                className="absolute left-0 w-full h-[2px] bg-violet-500 cursor-row-resize"
                onDragEnd={handleDrag("top")}
                style={{ top: `${cropData.topPosition}px` }}
                draggable
              ></div>
            </div>
          </div>
        )}
        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="CROP_PDF"
          />
        )}

        <div className="mt-20">
          <div className="mb-20">
            <PdfToolsLinks />
          </div>
          <Blog heading="PDF Merger Tool FAQs" BlogData={BlogData} />
        </div>
      </div>
    </div>
  );
};

export default CropPdf;
